import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    type BadgeProps,
    type ButtonKind,
    CardCheck,
    CardRadio,
    Description,
    FieldGroup,
    Group,
    Heading,
    type IconDefinition,
    Label,
    LoadingIndicator,
    ProgressiveImage,
    RadioGroup,
    renderIcon,
} from '@gitbook/spine-react';

import { createPalette, PaletteButton, PaletteCommandType } from '../Palette';

export type PickerKind = ButtonKind | 'cards';
export interface PickerItemData<Value = string> {
    label: string;
    description?: string;
    value: Value;
    icon?: IconDefinition;
    image?: string;
    disabled?: boolean;
    subtitle?: string;
    upgrade?: boolean;
    badge?: BadgeProps;
}

export type PickerSize = 'small' | 'medium' | 'large';

export type PickerProps<Value = string> = {
    /**
     * Text label to show next to the picker input.
     * @deprecated Use `FieldGroup` and `Label` components instead.
     */
    label?: string;
    /**
     * Longer text description to show next to the input.
     * @deprecated Use `FieldGroup` and `Description` components instead.
     */
    labelDescription?: React.ReactNode;
    /**
     * Vertically position the label/labelDescription.
     * @deprecated Use `FieldGroup` component instead.
     */
    vertical?: boolean;
    /**
     * Badge to show next to the label.
     * @deprecated Use `FieldGroup`, `Group` and `Label` components instead.
     */
    badge?: React.ReactNode;
    /** The picker can be used as a filter or a button */
    kind?: PickerKind;
    /** When the picker is a button it might have a leading icon */
    leadingIcon?: IconDefinition;
    /** Current selected value */
    value: Value;
    /** List of potential values that can be selected */
    items: PickerItemData<Value>[];
    /** Placeholder text to show when no value is selected */
    placeholder?: string;
    /** Callback when a value is selected */
    onValueChange: (value: Value) => void | Promise<void>;
    /** Style to pass to the overall container */
    className?: string;
    /** Size of the picker button */
    size?: PickerSize;
    /** Icon rendered on the left side */
    icon?: IconDefinition;
    /** Disable the use of this picker (default false) */
    disabled?: boolean;
    /** Add a search input to select item (default false) */
    search?: boolean;
    /** Label to use for the search input */
    searchLabel?: string;
    /**
     * Orientation of the picker (for cards).
     */
    orientation?: 'horizontal' | 'vertical';
};

/**
 * Picker for a value between different values.
 */
export function Picker<Value = string>(props: PickerProps<Value>): React.ReactElement {
    const { kind = 'secondary', ...pickerProps } = props;
    const {
        leadingIcon,
        value,
        items,
        placeholder,
        onValueChange,
        size,
        disabled,
        search,
        searchLabel,
        orientation,
        ...pickerFieldProps
    } = pickerProps;

    const isWithinField = props.label || props.labelDescription || props.icon || props.badge;

    const content =
        kind === 'cards' ? (
            <PickerCards
                value={value}
                onValueChange={onValueChange}
                items={items}
                orientation={orientation}
            />
        ) : (
            <PickerButton kind={kind} {...pickerProps} />
        );

    if (isWithinField) {
        return <PickerField {...pickerFieldProps}>{content}</PickerField>;
    }

    return content;
}

/** Picker as palette button */
function PickerButton<Value = string>(props: PickerProps<Value> & { kind?: ButtonKind }) {
    const {
        size = 'medium',
        disabled = false,
        search = false,
        searchLabel = 'Select item…',
        items,
    } = props;
    const activeItem = items.find((item) => item.value === props.value);
    const [loading, setLoading] = React.useState(false);

    return (
        <PaletteButton
            data-selectedcontent={activeItem ? '' : undefined}
            size={size}
            leadingIcon={
                loading ? <LoadingIndicator /> : activeItem ? activeItem.icon : props.leadingIcon
            }
            label={activeItem ? activeItem.label : props.placeholder || items[0]?.label}
            kind={props.kind}
            disabled={loading || disabled}
            palette={() =>
                createPalette({
                    search: search ? { placeholder: searchLabel } : undefined,
                    groups: [
                        {
                            key: 'items',
                            commands: items.map((item) => ({
                                key: String(item.value),
                                title: item.label,
                                icon: item.icon,
                                disabled: item.disabled,
                                subtitle: item.subtitle,
                                upgrade: item.upgrade,
                                badge: item.badge,
                                trigger: () => {
                                    const result = props.onValueChange(item.value);
                                    if (result) {
                                        setLoading(true);
                                        result.finally(() => setLoading(false));
                                    }
                                },
                                ...(item.value === props.value
                                    ? {
                                          type: PaletteCommandType.RADIO,
                                          selected: true,
                                      }
                                    : {
                                          selected: undefined,
                                      }),
                            })),
                        },
                    ],
                })
            }
            trailingIcon={Icon.ChevronDown}
            labelStyle="mr-auto"
            className={props.className}
        />
    );
}

/** Picker as cards */
function PickerCards<Value = string>(
    props: Pick<PickerProps<Value>, 'value' | 'onValueChange' | 'items' | 'orientation'>
) {
    const { value, onValueChange, items, orientation = 'horizontal' } = props;

    return (
        <RadioGroup
            className={clsx(orientation === 'horizontal' && 'self-center')}
            orientation={orientation}
            value={value ? assertStringValue(value) : null}
            onChange={(value) => onValueChange((value || undefined) as Value)}
        >
            {items.map((item) => (
                <CardRadioItem key={assertStringValue(item.value)} item={item} />
            ))}
        </RadioGroup>
    );
}

function CardRadioItem<Value = string>({ item }: { item: PickerItemData<Value> }) {
    return (
        <CardRadio
            value={assertStringValue(item.value)}
            /*
                Hack of min-w here that should go because "Picker"
                should not be used. Instead we should use `Form` and `RadioField`.
            */
            className="flex min-w-[234px] flex-1 items-center justify-between gap-4 p-4"
        >
            <div>
                {item.image && (
                    <ProgressiveImage src={item.image} width={48} height={48} className="mb-2" />
                )}
                <Heading className="text-ui-standout">{item.label}</Heading>
                {item.description ? (
                    <Description className="mt-1 text-ui-small">{item.description}</Description>
                ) : null}
            </div>
            <CardCheck />
        </CardRadio>
    );
}

/** If the Picker is within a field (ie: label, description, icon...) */
function PickerField(
    props: Pick<
        PickerProps,
        'badge' | 'label' | 'labelDescription' | 'icon' | 'vertical' | 'className'
    > & { children: React.ReactNode }
) {
    const { label, labelDescription, icon: IconComponent, badge, vertical = false } = props;

    return (
        <FieldGroup orientation={vertical ? 'vertical' : 'horizontal'} className={props.className}>
            <div className="flex items-center gap-4">
                {IconComponent ? (
                    <div className="flex shrink-0 text-muted">
                        {renderIcon(IconComponent, { size: 20 })}
                    </div>
                ) : null}
                {label && (
                    <Group>
                        <Group>
                            <Label>{label}</Label>
                            {badge}
                        </Group>
                        {labelDescription && <Description>{labelDescription}</Description>}
                    </Group>
                )}
            </div>
            {props.children}
        </FieldGroup>
    );
}

/**
 * Assert that value is a string (for compatibility with cards).
 */
function assertStringValue(value: unknown): string {
    if (typeof value !== 'string') {
        throw new Error('Value must be a string');
    }
    return value;
}
